<template>
	<div v-if="visible">
		<div
			v-show="!isMinimized"
			class="custom-dialog"
			:class="{ fullscreen: isFullScreen, compact: isCompactScreen }"
			:style="windowStyle"
		>
			<div class="dialog-header" :class="{ 'no-drag': isFullScreen }" @mousedown="startDrag">
				<div class="dialog-title" :title="title">{{ title }}</div>
				<div class="dialog-controls">
					<button
						v-if="showMinimize && minimizable"
						class="control-btn"
						type="button"
						title="最小化"
						@click="minimizeWindow"
					>
						<i class="el-icon-minus"></i>
					</button>
					<button
						v-if="showMaximize && resizable"
						class="control-btn"
						type="button"
						title="全屏"
						@click="toggleFullScreen"
					>
						<i class="el-icon-full-screen"></i>
					</button>
					<button
						v-if="showClose"
						class="control-btn control-btn--danger"
						type="button"
						title="关闭"
						@click="closeDialog"
					>
						<i class="el-icon-close"></i>
					</button>
				</div>
			</div>

			<div class="dialog-content">
				<slot></slot>
			</div>
		</div>

		<div
			v-show="isMinimized"
			class="taskbar-icon"
			:class="{ compact: isCompactScreen }"
			:style="taskbarStyle"
			@mousedown="startTaskbarDrag"
		>
			<div class="taskbar-content" @click="handleTaskbarClick">
				<div class="taskbar-avatar">
					<slot name="taskbar-avatar">
						<div class="default-avatar" :style="defaultTaskbarAvatarStyle">{{ taskbarAvatarText }}</div>
					</slot>
				</div>
				<div class="taskbar-info">
					<div class="taskbar-name">{{ title }}</div>
					<div class="taskbar-status">{{ taskbarStatus }}</div>
				</div>
			</div>
			<div class="taskbar-controls">
				<button class="taskbar-btn" type="button" title="恢复" @click="restoreWindow">
					<i class="el-icon-plus"></i>
				</button>
				<button class="taskbar-btn taskbar-btn--danger" type="button" title="关闭" @click="closeDialog">
					<i class="el-icon-close"></i>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { getAvatarColorIndex, getAvatarPlaceholderText } from '../../utils/avatarPlaceholder.js';

const AVATAR_PALETTE_LENGTH = 6;

export default {
	name: 'NonModalDialog',
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: '对话框'
		},
		width: {
			type: [Number, String],
			default: 800
		},
		height: {
			type: [Number, String],
			default: 600
		},
		showMinimize: {
			type: Boolean,
			default: true
		},
		showMaximize: {
			type: Boolean,
			default: true
		},
		showClose: {
			type: Boolean,
			default: true
		},
		taskbarStatus: {
			type: String,
			default: '运行中'
		},
		draggable: {
			type: Boolean,
			default: true
		},
		minimizable: {
			type: Boolean,
			default: true
		},
		resizable: {
			type: Boolean,
			default: true
		}
	},
	data() {
		const defaultViewport = typeof window === 'undefined'
			? { width: 1280, height: 720 }
			: { width: window.innerWidth, height: window.innerHeight };
		return {
			viewportGap: 12,
			taskbarGap: 12,
			viewport: defaultViewport,
			isMinimized: false,
			isFullScreen: false,
			isDragging: false,
			dragOffset: { x: 0, y: 0 },
			windowPosition: { x: 0, y: 0 },
			windowSize: { width: 800, height: 600 },
			isTaskbarDragging: false,
			taskbarDragOffset: { x: 0, y: 0 },
			taskbarPosition: { x: 0, y: 0 },
			taskbarDragFrame: null,
			taskbarHasDragged: false
		}
	},
	computed: {
		isCompactScreen() {
			return this.viewport.width <= 640;
		},
		windowStyle() {
			if (this.isFullScreen) {
				return {
					position: 'fixed',
					left: '0px',
					top: '0px',
					width: '100vw',
					height: '100vh',
					zIndex: 999
				}
			}
			return {
				position: 'fixed',
				left: `${this.windowPosition.x}px`,
				top: `${this.windowPosition.y}px`,
				width: `${this.windowSize.width}px`,
				height: `${this.windowSize.height}px`,
				zIndex: 999
			}
		},
		taskbarStyle() {
			const taskbarSize = this.getTaskbarSize();
			return {
				position: 'fixed',
				left: `${this.taskbarPosition.x}px`,
				top: `${this.taskbarPosition.y}px`,
				width: `${taskbarSize.width}px`,
				height: `${taskbarSize.height}px`,
				zIndex: 10000
			}
		},
		taskbarAvatarText() {
			return getAvatarPlaceholderText(this.title);
		},
		defaultTaskbarAvatarStyle() {
			const paletteIndex = getAvatarColorIndex(this.taskbarAvatarText, AVATAR_PALETTE_LENGTH) + 1;
			const fallback = 'var(--im-avatar-bg-default, var(--im-color-primary))';
			const color = `var(--im-avatar-bg-${paletteIndex}, ${fallback})`;
			return {
				background: `radial-gradient(circle at 30% 24%, rgba(var(--im-white-rgb), 0.18), transparent 52%), linear-gradient(145deg, rgba(var(--im-white-rgb), 0.08), rgba(var(--im-black-rgb), 0.12)), ${color}`
			};
		}
	},
	watch: {
		visible(newVal) {
			if (newVal) {
				this.resetWindowState();
			}
		},
		width() {
			this.syncWindowLayout({ recenter: true });
		},
		height() {
			this.syncWindowLayout({ recenter: true });
		}
	},
	mounted() {
		this.resetWindowState();
		window.addEventListener('resize', this.onViewportChange);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.onViewportChange);
		this.endDrag();
		this.endTaskbarDrag();
	},
	methods: {
		resolveDimension(value, fallback, viewportSize, minSize, maxRatio) {
			let pixels = fallback;
			if (typeof value === 'number' && Number.isFinite(value)) {
				pixels = value;
			} else if (typeof value === 'string') {
				const normalized = value.trim();
				if (normalized.endsWith('%')) {
					const percent = Number.parseFloat(normalized);
					if (Number.isFinite(percent)) {
						pixels = viewportSize * percent / 100;
					}
				} else {
					const directNumber = Number.parseFloat(normalized.replace('px', ''));
					if (Number.isFinite(directNumber)) {
						pixels = directNumber;
					}
				}
			}
			const finalMax = Math.max(Math.min(
				viewportSize * maxRatio,
				viewportSize - this.viewportGap * 2
			), 0);
			const finalMin = Math.min(minSize, finalMax || minSize);
			return Math.max(finalMin, Math.min(pixels, finalMax));
		},
		getWindowSize() {
			return {
				width: this.resolveDimension(this.width, 800, this.viewport.width, 320, 0.92),
				height: this.resolveDimension(this.height, 600, this.viewport.height, 260, 0.88)
			};
		},
		getTaskbarSize() {
			const maxWidth = Math.max(this.viewport.width - this.taskbarGap * 2, 0);
			const minWidth = Math.min(220, maxWidth);
			const width = Math.max(minWidth, Math.min(300, maxWidth));
			return {
				width,
				height: this.isCompactScreen ? 58 : 64
			};
		},
		getCenteredPosition(size) {
			return this.clampWindowPosition({
				x: (this.viewport.width - size.width) / 2,
				y: (this.viewport.height - size.height) / 2
			}, size);
		},
		getDefaultTaskbarPosition() {
			const taskbarSize = this.getTaskbarSize();
			return this.clampTaskbarPosition({
				x: this.viewport.width - taskbarSize.width - this.taskbarGap,
				y: this.viewport.height - taskbarSize.height - this.taskbarGap
			});
		},
		clampWindowPosition(position, size) {
			const maxX = Math.max(this.viewportGap, this.viewport.width - size.width - this.viewportGap);
			const maxY = Math.max(this.viewportGap, this.viewport.height - size.height - this.viewportGap);
			return {
				x: Math.max(this.viewportGap, Math.min(position.x, maxX)),
				y: Math.max(this.viewportGap, Math.min(position.y, maxY))
			};
		},
		clampTaskbarPosition(position) {
			const taskbarSize = this.getTaskbarSize();
			const maxX = Math.max(this.taskbarGap, this.viewport.width - taskbarSize.width - this.taskbarGap);
			const maxY = Math.max(this.taskbarGap, this.viewport.height - taskbarSize.height - this.taskbarGap);
			return {
				x: Math.max(this.taskbarGap, Math.min(position.x, maxX)),
				y: Math.max(this.taskbarGap, Math.min(position.y, maxY))
			};
		},
		syncViewport() {
			this.viewport = {
				width: window.innerWidth,
				height: window.innerHeight
			};
		},
		syncWindowLayout(options = {}) {
			const { recenter = false, resetTaskbar = false } = options;
			this.syncViewport();
			const size = this.getWindowSize();
			this.windowSize = size;
			if (!this.isFullScreen) {
				this.windowPosition = recenter
					? this.getCenteredPosition(size)
					: this.clampWindowPosition(this.windowPosition, size);
			}
			this.taskbarPosition = resetTaskbar
				? this.getDefaultTaskbarPosition()
				: this.clampTaskbarPosition(this.taskbarPosition);
		},
		resetWindowState() {
			this.isMinimized = false;
			this.isFullScreen = false;
			this.taskbarHasDragged = false;
			this.syncWindowLayout({ recenter: true, resetTaskbar: true });
		},
		closeDialog() {
			this.$emit('close');
		},
		toggleFullScreen() {
			if (!this.resizable) {
				return;
			}
			this.isFullScreen = !this.isFullScreen;
			if (!this.isFullScreen) {
				this.syncWindowLayout({ recenter: true });
			}
			this.$emit('fullscreen-change', this.isFullScreen);
		},
		startDrag(event) {
			if (!this.draggable || this.isFullScreen || event.target.closest('.dialog-controls')) {
				return;
			}
			this.isDragging = true;
			const rect = event.currentTarget.parentElement.getBoundingClientRect();
			this.dragOffset = {
				x: event.clientX - rect.left,
				y: event.clientY - rect.top
			};
			document.addEventListener('mousemove', this.onDrag);
			document.addEventListener('mouseup', this.endDrag);
			event.preventDefault();
		},
		onDrag(event) {
			if (!this.isDragging) {
				return;
			}
			this.windowPosition = this.clampWindowPosition({
				x: event.clientX - this.dragOffset.x,
				y: event.clientY - this.dragOffset.y
			}, this.windowSize);
		},
		endDrag() {
			this.isDragging = false;
			document.removeEventListener('mousemove', this.onDrag);
			document.removeEventListener('mouseup', this.endDrag);
		},
		minimizeWindow() {
			if (!this.minimizable) {
				return;
			}
			this.isMinimized = true;
			this.taskbarPosition = this.getDefaultTaskbarPosition();
			this.$emit('minimize');
		},
		restoreWindow() {
			this.isMinimized = false;
			this.taskbarHasDragged = false;
			this.syncWindowLayout();
			this.$emit('restore');
		},
		handleTaskbarClick() {
			if (!this.taskbarHasDragged) {
				this.restoreWindow();
			}
		},
		startTaskbarDrag(event) {
			if (event.target.closest('.taskbar-controls')) {
				return;
			}
			this.isTaskbarDragging = true;
			this.taskbarHasDragged = false;
			const rect = event.currentTarget.getBoundingClientRect();
			this.taskbarDragOffset = {
				x: event.clientX - rect.left,
				y: event.clientY - rect.top
			};
			document.addEventListener('mousemove', this.onTaskbarDrag);
			document.addEventListener('mouseup', this.endTaskbarDrag);
			document.addEventListener('mouseleave', this.endTaskbarDrag);
			document.body.style.userSelect = 'none';
			document.body.style.cursor = 'move';
			event.preventDefault();
		},
		onTaskbarDrag(event) {
			if (!this.isTaskbarDragging) {
				return;
			}
			this.taskbarHasDragged = true;
			if (this.taskbarDragFrame) {
				cancelAnimationFrame(this.taskbarDragFrame);
			}
			this.taskbarDragFrame = requestAnimationFrame(() => {
				this.taskbarPosition = this.clampTaskbarPosition({
					x: event.clientX - this.taskbarDragOffset.x,
					y: event.clientY - this.taskbarDragOffset.y
				});
			});
		},
		endTaskbarDrag() {
			this.isTaskbarDragging = false;
			document.removeEventListener('mousemove', this.onTaskbarDrag);
			document.removeEventListener('mouseup', this.endTaskbarDrag);
			document.removeEventListener('mouseleave', this.endTaskbarDrag);
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
			if (this.taskbarDragFrame) {
				cancelAnimationFrame(this.taskbarDragFrame);
				this.taskbarDragFrame = null;
			}
		},
		onViewportChange() {
			if (!this.visible) {
				this.syncViewport();
				return;
			}
			this.syncWindowLayout();
		}
	}
}
</script>

<style lang="scss" scoped>
.custom-dialog {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	background: var(--im-chat-bg);
	border-radius: 8px;
	box-shadow: 0 14px 30px -22px rgba(var(--im-black-rgb), 0.34);
	border: 1px solid rgba(var(--im-black-rgb), 0.08);
	overflow: hidden;
	user-select: none;

	&.fullscreen {
		border-radius: 0;
		border: none;
		box-shadow: none;
	}

	.dialog-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		min-height: 48px;
		padding: 0 10px 0 14px;
		border-bottom: 1px solid rgba(var(--im-black-rgb), 0.08);
		background: var(--im-chat-bg);
		cursor: move;

		&.no-drag {
			cursor: default;
		}
	}

	.dialog-title {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 14px;
		font-weight: 600;
		color: var(--im-text-color-primary);
	}

	.dialog-controls {
		display: inline-flex;
		align-items: center;
		gap: 4px;
	}

	.control-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		padding: 0;
		border: 0;
		border-radius: 6px;
		background: transparent;
		color: var(--im-text-color-light);
		cursor: pointer;
		transition: background-color 0.2s ease, color 0.2s ease;

		&:hover {
			background: rgba(var(--im-primary-rgb), 0.08);
			color: var(--im-color-primary);
		}

		&.control-btn--danger:hover {
			background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.1);
			color: var(--im-color-danger);
		}
	}

	.dialog-content {
		flex: 1;
		min-height: 0;
		overflow: hidden;
		background: var(--im-chat-bg);
	}
}

.taskbar-icon {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: 12px;
	height: 64px;
	padding: 10px 12px;
	border-radius: 8px;
	background: var(--im-chat-bg);
	box-shadow: 0 14px 30px -24px rgba(var(--im-black-rgb), 0.3);
	border: 1px solid rgba(var(--im-black-rgb), 0.08);
	cursor: move;
	user-select: none;

	.taskbar-content {
		display: flex;
		align-items: center;
		gap: 12px;
		flex: 1;
		min-width: 0;
		cursor: pointer;
	}

	.taskbar-avatar {
		width: 40px;
		height: 40px;
		border-radius: 999px;
		overflow: hidden;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-primary-rgb), 0.12);

		.default-avatar {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			color: var(--im-avatar-text, var(--im-text-color-white));
			font-size: 14px;
			font-weight: 600;
			line-height: 1;
			box-shadow: inset 0 0 0 1px var(--im-avatar-border, rgba(var(--im-black-rgb), 0.06));
		}
	}

	.taskbar-info {
		flex: 1;
		min-width: 0;
	}

	.taskbar-name,
	.taskbar-status {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.taskbar-name {
		font-size: 14px;
		font-weight: 600;
		color: var(--im-text-color-primary);
	}

	.taskbar-status {
		margin-top: 2px;
		font-size: 12px;
		color: var(--im-text-color-light);
	}

	.taskbar-controls {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		flex-shrink: 0;
	}

	.taskbar-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 30px;
		height: 30px;
		padding: 0;
		border: 0;
		border-radius: 6px;
		background: transparent;
		color: var(--im-text-color-light);
		cursor: pointer;
		transition: background-color 0.2s ease, color 0.2s ease;

		&:hover {
			background: rgba(var(--im-primary-rgb), 0.08);
			color: var(--im-color-primary);
		}

		&.taskbar-btn--danger:hover {
			background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.1);
			color: var(--im-color-danger);
		}
	}
}

@media (max-width: 640px) {
	.custom-dialog {
		border-radius: 8px;

		.dialog-header {
			min-height: 44px;
			padding: 0 8px 0 12px;
		}

		.control-btn {
			width: 28px;
			height: 28px;
			border-radius: 6px;
		}
	}

	.taskbar-icon {
		gap: 10px;
		height: 58px;
		padding: 8px 10px;
		border-radius: 8px;

		.taskbar-avatar {
			width: 36px;
			height: 36px;
		}
	}
}
</style>
